/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { BarChart3, Users, MessageSquare, Handshake, Bot, Newspaper } from 'lucide-react';

const CATEGORY_COLORS = {
  medici: 'var(--wl-primary)',
  infermieri: '#DB2777',
  oss: '#7C3AED',
  general: '#94A3B8',
  private: '#94A3B8',
  unknown: '#64748B',
};

const CATEGORY_LABEL = {
  medici: 'Medici',
  infermieri: 'Infermieri',
  oss: 'OSS',
  general: 'Generale',
  private: 'Privata',
  unknown: 'Sconosciuta',
};

const CategoryBar = ({ data, title, Icon }) => {
  const entries = Object.entries(data || {});
  const total = entries.reduce((sum, [, v]) => sum + v, 0);
  if (total === 0) return null;
  return (
    <div className="space-y-2" data-testid={`analytics-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" /> {title}
      </div>
      <div className="flex h-8 rounded-lg overflow-hidden bg-secondary">
        {entries.map(([cat, count]) => {
          const pct = (count / total) * 100;
          if (pct === 0) return null;
          return (
            <div
              key={cat}
              className="h-full flex items-center justify-center text-[10px] font-semibold text-white transition-all"
              style={{ width: `${pct}%`, backgroundColor: CATEGORY_COLORS[cat] || '#64748B', minWidth: pct > 5 ? undefined : 0 }}
              title={`${CATEGORY_LABEL[cat] || cat}: ${count}`}
            >
              {pct >= 8 && `${Math.round(pct)}%`}
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2 text-[11px]">
        {entries.map(([cat, count]) => (
          <span key={cat} className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[cat] || '#64748B' }} />
            <span className="font-semibold">{CATEGORY_LABEL[cat] || cat}</span>
            <span className="text-muted-foreground">{count}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const AdminAnalyticsCard = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/admin/analytics/categories');
        setData(r.data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <Card className="p-4 rounded-2xl animate-pulse h-32" data-testid="admin-analytics-loading" />;
  if (!data) return null;

  // 7-day daily posts chart
  const days = Object.keys(data.daily_posts_7d || {}).sort();
  const dailyTotals = days.map(d => Object.values(data.daily_posts_7d[d]).reduce((a, b) => a + b, 0));
  const maxDaily = Math.max(1, ...dailyTotals);

  return (
    <Card className="p-4 rounded-2xl" data-testid="admin-analytics-card">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <BarChart3 className="h-4 w-4" />
        </div>
        <div>
          <div className="font-display font-semibold text-sm">{t('analyticsTitle')}</div>
          <div className="text-[11px] text-muted-foreground">{t('analyticsSubtitle')}</div>
        </div>
      </div>

      <div className="space-y-4">
        <CategoryBar data={data.users_by_category} title={t('users')} Icon={Users} />
        <CategoryBar data={data.posts_by_category} title={t('posts')} Icon={Newspaper} />
        <CategoryBar data={data.initiatives_by_category} title={t('bacheca')} Icon={Handshake} />
        <CategoryBar data={data.messages_by_category} title={t('chat')} Icon={MessageSquare} />
        <CategoryBar data={data.ai_messages_by_category} title={t('aiAssistant')} Icon={Bot} />

        {days.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{t('trend7d')}</div>
            <div className="flex items-end gap-1 h-16">
              {days.map((d, i) => (
                <div
                  key={d}
                  className="flex-1 flex flex-col items-center gap-1"
                  title={`${d}: ${dailyTotals[i]}`}
                >
                  <div
                    className="w-full rounded-t bg-primary/70 min-h-[2px]"
                    style={{ height: `${(dailyTotals[i] / maxDaily) * 100}%` }}
                  />
                  <div className="text-[9px] text-muted-foreground">{d.slice(5)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default AdminAnalyticsCard;
